import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { formatCurrency } from "../../utils/helpers";
import { useState } from "react";
import constants from "../../utils/constants";
import { PencilSquareIcon } from "@heroicons/react/24/solid";
import { NumberInput, SelectInput, TextInput } from "../Inputs";
import { Save } from "react-bootstrap-icons";
import { PrimaryButton } from "../Buttons";
import { useTenantTheme } from "../../context/useSystemTenantStore";

const YEAR_IN_DAYS = 365;
export default function TrialOfferDisplay({product, setProduct}) {
  const [selectedTrialOffer, setSelectedTrialOffer] = useState();
  const [selectedIndex, setSelectedIndex] = useState();
  const handleOfferChange = (property, value) => {
    if (!selectedTrialOffer) return;
    setSelectedTrialOffer({
      ...selectedTrialOffer,
      [property]: value,
    });
  };
  function createNewOffer() {
    let newOffer = {
      id: 0,
      name: "",
      productId: product?.id,
      price: 0,
      durationCount: 7,
      durationType: constants.DURATION_TYPES[0].value, 
      daysSinceLastOffer: YEAR_IN_DAYS
    };
    let offers = [...product?.trialOffers, newOffer];
    setProduct({...product, trialOffers: offers});
    setSelectedTrialOffer(newOffer);
    setSelectedIndex(offers.length -1);
  }
  function saveOffer() {
    let newOffers = [];
    let productOffers = Array.isArray(product?.trialOffers) ? product.trialOffers : [];
    if (Number.isInteger(selectedIndex)) {
      productOffers.splice(selectedIndex, 1, selectedTrialOffer);
      newOffers = productOffers;
    }
    else{                    
      newOffers = [...productOffers, selectedTrialOffer];
    }
    setProduct({...product, trialOffers: newOffers});
    setSelectedTrialOffer(undefined);
    setSelectedIndex(undefined);
  }
  function deselectOffer() {
    setSelectedTrialOffer(undefined);
    setSelectedIndex(undefined);
  }
  function removeOffer(idx) {
    let newOffers = product?.trialOffers?.filter((x, i) => (i !== idx));
    setProduct({...product, trialOffers: newOffers});
  }
  return (
    <div>
      <div className="flex flex-row justify-between mb-6">
        <div className="text-xl font-semibold">Trial Offers</div>
        <div className="w-32">
          <PrimaryButton text={"Add Offer"} onClick={createNewOffer} />
        </div>
      </div>
      <div className={!!selectedTrialOffer ? "mb-14" : ""}>
        <Headers offerSelected={!!selectedTrialOffer}/>
        {product?.trialOffers && product.trialOffers.length && product.trialOffers.map((offer, idx) => {
          return idx === selectedIndex && !!selectedTrialOffer
          ? (<EditOffer offer={selectedTrialOffer} idx={idx} handleOfferChange={handleOfferChange} saveOffer={saveOffer} deselectOffer={deselectOffer} />)
          : (<ReadOnlyOffer offer={offer} idx={idx} setSelectedTrialOffer={setSelectedTrialOffer} setSelectedIndex={setSelectedIndex} removeOffer={removeOffer}  offerSelected={!!selectedTrialOffer}/>);
        })}
      </div>
    </div>
  );
}

function EditOffer({offer, idx, handleOfferChange, saveOffer, deselectOffer}) {
  const theme = useTenantTheme();
  return (
    <div 
      key={`product-offer-${idx}`} 
      className="px-2 py-4 flex flex-row hover:text-slate-500 hover:bg-gray-200 gap-x-4 rounded-md"
    >
      <div className="flex flex-col flex-1">
        <TextInput
          // label={"Name"}
          value={offer.name}
          onChange={(e) => handleOfferChange("name", e.target.value)}
          required
          maxLength={250}
        />
      </div>
      <div className="flex flex-col flex-1">
        <NumberInput
          // label="Price in pennies"
          value={offer.price ?? ""}
          onChange={(e) => handleOfferChange("price", e.target.valueAsNumber)}
          required
          isCurrency
          isPennies
        />
      </div>  
      <div className="flex flex-col flex-1">
        <NumberInput
          // label="Duration Amount"
          value={offer.durationCount ?? ""}
          onChange={(e) => handleOfferChange("durationCount", e.target.valueAsNumber)}
          min={0}
          required
        />
      </div>
      <div className="flex flex-col flex-1">
        <SelectInput
          // label="Duration Type"
          options={constants.DURATION_TYPES}
          value={constants.DURATION_TYPES.find(x => x.value === offer.durationType)}
          onChange={(e) => handleOfferChange("durationType", e.value)}
          required
        />
      </div>
      <div className="flex flex-col flex-1">
        <NumberInput
          // label={"Days Since Last Offer Threshold"}
          value={offer.daysSinceLastOffer ?? ""}
          onChange={(e) => handleOfferChange("daysSinceLastOffer", e.target.valueAsNumber)}
          min={0}
          required
        />
      </div>
      <div className="flex flex-row gap-x-4 w-20">
        <CheckIcon
          title="Save"
          className="h-6 w-6 text-white bg-primaryColor rounded-md p-1 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            saveOffer();
          }}
          style={{ backgroundColor: theme?.primaryHexColor }}
        />
        <XMarkIcon
          title="Cancel"
          className="h-6 w-6 text-gray-700 bg-gray-300 rounded-md p-1 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            deselectOffer();
          }}
        />
      </div>
    </div>
  );
}

function ReadOnlyOffer({offer, idx, setSelectedTrialOffer, setSelectedIndex, removeOffer, offerSelected}) {
  const theme = useTenantTheme();
  const duration = constants.DURATION_TYPES.find(x => x.value === offer?.durationType);
  return (
    <div 
      key={`product-offer-${idx}`} 
      className="px-2 py-4 flex flex-row hover:text-slate-500 hover:bg-gray-200 gap-x-4 rounded-md"
    >
      <div className="flex flex-1">{offer.name}</div>
      <div className="flex flex-1">{formatCurrency(offer.price, true)}</div>
      {offerSelected
      ? (
        <>
          <div className="flex flex-1">{offer.durationCount}</div>
          <div className="flex flex-1">{offer.durationCount === 1 ? duration?.singular : duration?.label}</div>
        </>
      )
      : (<div className="flex flex-1">{offer.durationCount} {offer.durationCount === 1 ? duration?.singular : duration?.label}</div>)}
      <div className="flex flex-1">{offer.daysSinceLastOffer} {offer.durationCount === 1 ? "Day" : "Days"}</div>
      <div className="flex flex-row gap-x-4 w-20">
        <PencilSquareIcon
          title="Edit"
          className="h-6 w-6 text-white bg-primaryColor rounded-md p-1 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedTrialOffer(offer);
            setSelectedIndex(idx);
          }}
          style={{ backgroundColor: theme?.primaryHexColor }}
        />
        <XMarkIcon
          title="Remove"
          className="h-6 w-6 text-white bg-delete rounded-md p-1 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            removeOffer(idx);
          }}
        />
      </div>
    </div>
  );
}

function Headers({offerSelected}) {
  const theme = useTenantTheme();
  return (
    <div 
      key={`headers`} 
      className="px-2 py-4 flex flex-row text-white bg-primaryColor gap-x-4 rounded-sm"
      style={{ backgroundColor: theme?.primaryHexColor }}
    >
      <div className="flex flex-1">Name</div>
      <div className="flex flex-1">Price</div>
      {offerSelected
      ? (
        <>
          <div className="flex flex-1">Duration amount</div>
          <div className="flex flex-1">Duration type</div>
        </>
      )
      : (<div className="flex flex-1">Duration</div>)}
      <div className="flex flex-1">Days between offer eligibility</div>
      <div className="flex w-20">
      </div>
    </div>
  );
}